// @copilot Ensure that all release notes added here are written for end-user or client consumption.
// They should be friendly, clear, and non-technical to ensure easy understanding.

import { ChangelogData } from '@eltoro-ui/eltoro-release-notes'

export const releaseNotes: ChangelogData = {
  title: 'Release Notes',
  list: [
    {
      version: '10.2.13',
      date: 'November 3rd 2026',
      fixes: [],
      features: [],
      improvements: [
        {
          message:
            'The Data Processing Agreement has been updated to the latest version. You can view or download it from the link at the bottom of any page.',
        },
      ],
    },
    {
      version: '10.2.12',
      date: 'October 26th 2026',
      fixes: [
        {
          message:
            'The political pre-payment reminder no longer covers the Acceptable Use Policy and Data Processing Agreement links at the bottom of the page while creating or editing a campaign. On smaller screens, the reminder now appears at the top of each step instead.',
        },
      ],
      features: [],
      improvements: [],
    },
    {
      version: '10.2.11',
      date: 'May 19th 2026',
      fixes: [
        {
          message:
            'Related audience names now truncate correctly so they fit neatly within the available space.',
        },
      ],
      features: [],
      improvements: [
        {
          message:
            'You can no longer exclude every audience on an order line at the same time. At least one audience must remain active to ensure your campaign has valid targeting. Order lines where all audiences are excluded will be flagged and blocked from submission until an active audience is included.',
        },
        {
          message:
            'Native Banner ad creation now enforces updated image and content guidelines. Uploaded images must meet accepted dimensions (1200×627, 600×600, or 800×600), stay under 3 MB, and have a minimum resolution of 72 PPI — with 144 PPI recommended for best quality. Field character limits for headlines, descriptions, and calls to action have also been refined to better align with native ad platform standards.',
        },
      ],
    },
    {
      version: '10.2.10',
      date: 'April 16th 2026',
      fixes: [
        {
          message:
            'When submitting your order lines for review, you can no longer accidentally refresh the page or navigate away during payment processing. A full-screen overlay now prevents any actions until the submission is complete, ensuring your order lines are successfully submitted.',
        },
      ],
      features: [],
      improvements: [],
    },
    {
      version: '10.2.9',
      date: 'April 8th 2026',
      fixes: [
        {
          message:
            'Updated order line name generation to include entire campaign name including underscores',
        },
        {
          message:
            'Digital New Mover audiences interface improved loading indicators and more reliable request handling.',
        },
        {
          message: 'Re-enabled copy order line to a new campaign.',
        },
        {
          message:
            'You can now copy order lines into archived political campaigns.',
        },
        {
          message:
            'Fixed an issue where a video file could bypass OTT upload validation by being renamed from .mov to .mp4. Only valid MP4 files are now accepted.',
        },
        {
          message:
            "When creating a child organization, you'll now see a clear message if the name is unavailable, making it easier to choose a different name.",
        },
      ],
      features: [],
      improvements: [],
    },
    {
      version: '10.2.8',
      date: 'March 23rd 2026',
      fixes: [
        {
          message:
            "Fixed login session issues. You'll now stay logged in more reliably, even when your browser tab is inactive.",
        },
        {
          message:
            'Political transparency forms now copy faster when duplicating or creating order lines.',
        },
      ],
      features: [],
      improvements: [],
    },
    {
      version: '10.2.7',
      date: 'March 18th 2026',
      fixes: [
        {
          message:
            'Fixed an issue where uploading multiple custom audiences at once could result in incorrect pricing.',
        },
      ],
      features: [],
      improvements: [],
    },
    {
      version: '10.2.6',
      date: 'March 2nd 2026',
      fixes: [],
      features: [],
      improvements: [
        {
          message:
            'Updated OTT creative requirements to ensure higher quality video ads. Videos must now be at least 1920x1080 resolution with an exact 16:9 aspect ratio.',
        },
        {
          message:
            'OTT upload now shows multiple errors and warnings at once, making it easier to identify and fix all issues with your video or image files in a single upload attempt.',
        },
      ],
    },
    {
      version: '10.2.5',
      date: 'January 12th 2026',
      fixes: [
        {
          message:
            'Temporarily disabled the Retargeting audience upload option while we complete the transition to our new pixel script.',
        },
      ],
      features: [],
      improvements: [
        {
          message:
            'Pixel subscription pages now include a quick link to edit your SFTP path from the appropriate page.',
        },
      ],
    },
    {
      version: '10.2.4',
      date: 'December 2nd 2025',
      fixes: [
        {
          message:
            'Advanced Reporting now works more smoothly. Campaign names display correctly, custom date ranges select reliably, date picker messages are easier to understand, and numeric values appear in a consistent format. Campaign reports also show both total and served impressions, and unused status filters have been removed. The performance chart now scales automatically to match your data for better readability.',
        },
        {
          message:
            'The Copy Order Line button is disabled when an order line is locked. A tooltip now explains why it cannot be copied, reducing confusion.',
        },
        {
          message:
            'Web-to-Home subscription metrics will no longer show duplicate entries for the same reporting period. When multiple runs occur for the same date, only the most recent result is displayed.',
        },
        {
          message:
            'Advanced Reporting: Selecting Include Child Organizations now fetches stats data for the first layer of child org order lines/campaigns.',
        },
      ],
      features: [],
      improvements: [
        {
          message:
            'Switching the Owners or Renters toggles under New Mover no longer triggers unnecessary loading spinners.',
        },
        {
          message:
            'Device audiences now create related household targeting audiences, allowing you to reach entire households connected to specific devices for broader campaign reach.',
        },
        {
          message:
            'Admins can now create household-based device audiences for Venue Replay campaigns, expanding targeting capabilities for location-based advertising.',
          admin: true,
        },
        {
          message:
            'Deleting order lines now requires confirmation to prevent accidental deletions. You can cancel by clicking the Cancel button or pressing the Escape key.',
        },
        {
          message:
            'Order line IDs are now displayed in the cart view with a convenient copy button, making it easier to reference or share order line information for support requests or tracking purposes.',
        },
      ],
    },
    {
      version: '10.2.3',
      date: 'November 17th 2025',
      fixes: [
        {
          message:
            'Map previews now display for Map Polygon and Venue Replay audiences even while they are still processing, allowing you to visualize your target area immediately.',
        },
        {
          message:
            'New Mover audience creation now allows you to select Pre Mover and Escrow Mover types alongside different Post Mover options (Owners only, Renters only, or Both), giving you more flexibility in targeting households at various stages of their moving journey.',
        },
        {
          message:
            'When uploading an audience file, you can now pick more than one Custom ID column. This helps you include all of your identifiers in one step',
        },
      ],
      features: [],
      improvements: [
        {
          message:
            'Organizations in collections or with notifications disabled will now see a helpful message when attempting to submit support requests or change their organization name, directing them to contact our support team directly for assistance.',
          admin: true,
        },
        {
          message:
            'If your billing setup isn’t complete, credit card management will now be blocked with a clear message explaining what needs to happen first.',
        },
      ],
    },
    {
      version: '10.2.2',
      date: 'November 11th 2025',
      fixes: [
        {
          message:
            'UTM tracking parameters can now only be added after entering a valid click-through URL in the creative editor, preventing errors and ensuring your campaign links work correctly.',
        },
        {
          message:
            'Improved visual consistency and readability in both dark and light modes.',
        },
        {
          message:
            'You can now successfully copy order lines to existing campaigns, and political settings (if applicable) are properly maintained on the target order lines, ensuring compliance requirements stay intact.',
        },
        {
          message:
            'Resolved scaling issues in the campaign performance chart, improved tooltip display, and fixed light mode bug in reporting charts.',
        },
      ],
      features: [
        {
          message:
            'New Create Audience Option Available: Custom Audience (Device).',
        },
      ],
      improvements: [
        {
          message:
            'Searching for organizations in the sidebar menu is now easier with clearer error messages that guide you when you need to add more characters to your search.',
        },
        {
          message:
            'Advance Reporting tables now display data more consistently with improved column sizing and better layout stability, making it easier to read and compare your campaign performance metrics.',
          admin: true,
        },
        {
          message:
            'Reporting stats now automatically display in your local timezone for easier data analysis.',
        },
        {
          message:
            'The reports dashboard now displays filter options even when you have no active campaigns, where they were previously hidden.',
        },
        {
          message:
            'Improved help text for locked order lines to better explain how to request changes.',
        },
        {
          message:
            'Creative uploads now handle special characters in filenames better while preserving original names.',
        },
        {
          message:
            'Removed duplicate Organization Note displays from the Campaign Creation workflow.',
          admin: true,
        },
        {
          message:
            'Added ability to upload CSV and ZIP files for support requests, supporting files up to 50mb.',
        },
        {
          message: 'Minor adjustments made to the Political Transparency Form.',
        },
        {
          message:
            'Political campaigns can now use modern HTML5 creative formats, giving you access to more interactive and engaging ad options for your political advertising campaigns.',
        },
        {
          message:
            'Reports page now shows clearer error messages when issues occur. Filter changes are temporarily disabled while data is loading to prevent conflicts, and child organization reporting now correctly displays statistics across all selected child organizations.',
        },
      ],
    },
    {
      version: '10.2.1',
      date: 'October 15th 2025',
      fixes: [
        {
          message:
            "Order line performance statistics now show accurate 'Today' and 'Yesterday' values in your dashboard, giving you reliable real-time insights.",
        },
      ],
      features: [],
      improvements: [
        {
          message:
            'You can now hide audiences from your library when saving Retargeting and Digital New Mover audiences, keeping lists cleaner and focused.',
          admin: true,
        },
        {
          message:
            'You can now view product updates and announcements directly inside the app.',
        },
        {
          message:
            'Political advertisers in New York and New Jersey can now easily upload their state election registration forms directly through the transparency form, streamlining compliance requirements.',
        },
        {
          message:
            'Reports timeframe selector now provides better guidance when invalid date ranges are selected, and streamlined CSV export options for easier data downloads.',
        },
      ],
    },
    {
      version: '10.2.0',
      date: 'October 7th 2025',
      fixes: [
        {
          message:
            'Fixed an issue where some organization links didn’t open the correct child organization page.',
        },
      ],
      features: [
        {
          message:
            'Create Venue Replays audiences straight from a map polygon—no extra steps needed.',
        },
      ],
      improvements: [
        {
          message:
            'Non-billable child organizations can now create their own child orgs without choosing a billing contact, simplifying setup.',
        },
        {
          message: 'Reports now load faster for smoother navigation.',
        },
        {
          message:
            'You can now target homeowners or renters directly when creating New Movers audiences.',
        },
        {
          message:
            'The Creative Editor now has a cleaner table layout that makes it easier to organize and edit creatives.',
        },
      ],
    },
    {
      version: '10.1.0',
      date: 'October 2nd 2025',
      fixes: [
        {
          message:
            'Prevented lowering an order line’s impression goal below what has already been delivered, avoiding accidental under-reporting.',
        },
      ],
      features: [
        {
          message:
            'When submitting a support request, you can now choose who should get notified.',
        },
      ],
      improvements: [
        {
          message:
            'Non-billable child organizations can now create their own child orgs without choosing a billing contact, simplifying setup.',
        },
      ],
    },
  ],
}
